/**
 * E-GTCC-VIEW-TEN1 — read client of the GTCC view "Tenants & Access".
 *
 * The Owner order for this view is "1:1 nach Referenz" and "ausschliesslich
 * bestehende Admin-Endpunkte der alten Konsole — messen und wiederverwenden,
 * keine neuen Schreibpfade". Both are honoured literally: this module adds no
 * endpoint, no route and no write. It reads exactly the paths gtcc1 already
 * reads (measured in `GtccTenantsView.tsx:6-21`, `TenantDevicesCard.tsx:113`
 * and `useInvitations.ts:64`) and does the two things the old view does not:
 * it maps every failure onto one discriminated union so no tab ever sees an
 * `ApiError`, and it owns the `["gtcc", "ten", "view", …]` query keys of the
 * GTCC key inventory.
 *
 * The keys carry the extra `view` segment on purpose. `GtccTenantsView` keeps
 * its own `["gtcc", "ten", …]` keys with a throwing `queryFn`; the same key
 * with two different fetchers would be a cache collision, so the two surfaces
 * stay side by side until D-GTCC-SHELL1 merges and the old view goes.
 *
 * No value is computed here. Lifecycle, launch verdicts and AI readiness
 * arrive decided by the API; where a reference value has no source at all the
 * tab writes "nicht messbar" instead of inventing one (Owner decisions
 * P8/P9/P10).
 */
import { ApiError, apiGet } from "../../lib/api.js";
import type { TenantLifecycleVerb } from "../../tenants/tenantLifecycle.js";

/* ---------------------------------------------------------------- paths */

export const TEN_TENANTS_PATH = "/v1/admin/tenants";
export const tenTenantPath = (tenantId: string): string => `/v1/admin/tenants/${tenantId}`;
export const tenMembersPath = (tenantId: string): string => `/v1/admin/tenants/${tenantId}/members`;
export const tenInvitationsPath = (tenantId: string): string =>
  `/v1/admin/tenants/${tenantId}/invitations`;
export const tenDevicesPath = (tenantId: string): string => `/v1/admin/tenants/${tenantId}/devices`;
export const tenLaunchStatusPath = (tenantId: string): string =>
  `/v1/admin/tenants/${tenantId}/launch-status`;
export const tenAiCapabilitiesPath = (tenantId: string): string =>
  `/v1/admin/tenants/${tenantId}/ai-capabilities`;
export const tenAiUsagePath = (tenantId: string): string =>
  `/v1/admin/tenants/${tenantId}/ai-usage`;

/* --------------------------------------------------------------- result */

/**
 * Same discriminated union the cockpit and the data plane already use
 * (`api/fleet.ts:167`, `api/dplane.ts:87`). The tenant routes are guarded by
 * `requirePlatformOwner("tenants")` and answer 403 when the caller is not a
 * platform owner; a 404 means the tenant itself is gone, which for a view
 * bound to a selected tenant is the honest "surface not available" case.
 */
export type TenResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | {
      readonly ok: false;
      readonly kind: "upstream";
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

/** Wraps one existing read; never swallows, always classifies. */
export async function readTen<T>(load: () => Promise<T>): Promise<TenResult<T>> {
  try {
    return { ok: true, data: await load() };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, requestId } = err;
      if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      return { ok: false, kind: "upstream", httpStatus: status, requestId };
    }
    const message = err instanceof Error ? err.message : "gtcc ten fetch failed";
    return { ok: false, kind: "transport_error", message };
  }
}

/* ----------------------------------------------------------------- types */

/**
 * The closed lifecycle set of the server state machine
 * (`tenants/tenantLifecycle.ts:8`). The reference draws a `.dot` per row; the
 * tone is derived from this set only, never from a free-text status.
 */
export const TEN_LIFECYCLE_STATUSES = [
  "trial",
  "active",
  "paused",
  "suspended",
  "cancelled",
  "expired",
] as const;
export type TenLifecycleStatus = (typeof TEN_LIFECYCLE_STATUSES)[number];

export type TenTenantType = "platform_only" | "managed_hardware" | "hybrid";

export interface TenTenantRow {
  readonly id: string;
  readonly displayName?: string | null;
  readonly slug?: string | null;
  readonly type?: TenTenantType;
  readonly subscriptionStatus?: TenLifecycleStatus;
  readonly trialUntil?: string | null;
  readonly maxDevices?: number | null;
  readonly maxVehicles?: number | null;
  readonly maxUsers?: number | null;
  readonly createdAt?: string;
}

interface TenTenantsResponse {
  readonly items?: ReadonlyArray<TenTenantRow> | null;
}

export interface TenEntitlements {
  readonly devices?: {
    readonly self_register?: string | null;
    readonly approval_required?: boolean | null;
    readonly imei_edit?: string | null;
    readonly delete?: string | null;
  } | null;
  readonly managed_hardware_only?: boolean | null;
  readonly allowed_vendors?: ReadonlyArray<string> | null;
}

export interface TenTenantDetail extends TenTenantRow {
  readonly validUntil?: string | null;
  readonly entitlements?: TenEntitlements | null;
  readonly resolvedEntitlements?: TenEntitlements | null;
}

interface TenTenantDetailResponse {
  readonly tenant?: TenTenantDetail | null;
}

export interface TenMemberRow {
  readonly id: string;
  readonly email?: string | null;
  readonly displayName?: string | null;
  readonly role?: string | null;
  readonly status?: string | null;
}

interface TenMembersResponse {
  readonly items?: ReadonlyArray<TenMemberRow> | null;
}

export interface TenInvitationRow {
  readonly id: string;
  readonly email?: string | null;
  readonly displayName?: string | null;
  readonly role?: string | null;
  readonly status?: string | null;
  readonly invitedByEmail?: string | null;
  readonly tokenExpiresAt?: string | null;
  readonly createdAt?: string | null;
}

interface TenInvitationsResponse {
  readonly items?: ReadonlyArray<TenInvitationRow> | null;
}

export interface TenDevicesSummary {
  readonly total: number;
  readonly capped: boolean;
}

interface TenDevicesResponse {
  readonly items?: ReadonlyArray<unknown> | null;
  readonly total?: number | null;
  readonly capped?: boolean | null;
}

export type TenLaunchItemId =
  | "tenant_exists"
  | "subscription_active"
  | "first_admin_invited"
  | "first_admin_active"
  | "vehicle_count"
  | "device_count"
  | "latest_email_delivery";
export type TenLaunchItemStatus = "ready" | "needs_action" | "blocked" | "manual";

export interface TenLaunchStatus {
  readonly overall?: TenLaunchItemStatus;
  readonly items?: ReadonlyArray<{
    readonly id: TenLaunchItemId;
    readonly status: TenLaunchItemStatus;
  }> | null;
  readonly computedAt?: string | null;
}

export interface TenAiCapabilities {
  readonly gfiAiQuestionsEnabled?: boolean;
  readonly aiReportsEnabled?: boolean;
  readonly mode?: "disabled" | "dry_run" | "enabled";
  readonly gfiProvider?: string | null;
  readonly gfiModel?: string | null;
  readonly reportsProvider?: string | null;
  readonly reportsModel?: string | null;
}

interface TenAiCapabilitiesResponse {
  readonly aiCapabilities?: TenAiCapabilities | null;
}

export interface TenAiUsage {
  readonly costVerified?: boolean;
  readonly totals?: {
    readonly invocations?: number;
    readonly dryRunCount?: number;
    readonly providerCalledCount?: number;
    readonly okCount?: number;
    readonly estimatedCostMicros?: number;
  } | null;
}

interface TenAiUsageResponse {
  readonly usage?: TenAiUsage | null;
}

/* -------------------------------------------------------------- fetchers */

export function fetchTenTenants(
  signal?: AbortSignal,
): Promise<TenResult<ReadonlyArray<TenTenantRow>>> {
  return readTen(async () => {
    const response = await apiGet<TenTenantsResponse>(TEN_TENANTS_PATH, undefined, signal);
    return asList(response.items);
  });
}

export function fetchTenTenant(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<TenTenantDetail | null>> {
  return readTen(async () => {
    const response = await apiGet<TenTenantDetailResponse>(
      tenTenantPath(tenantId),
      undefined,
      signal,
    );
    return response.tenant ?? null;
  });
}

export function fetchTenMembers(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<ReadonlyArray<TenMemberRow>>> {
  return readTen(async () => {
    const response = await apiGet<TenMembersResponse>(tenMembersPath(tenantId), undefined, signal);
    return asList(response.items);
  });
}

export function fetchTenInvitations(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<ReadonlyArray<TenInvitationRow>>> {
  return readTen(async () => {
    const response = await apiGet<TenInvitationsResponse>(
      tenInvitationsPath(tenantId),
      undefined,
      signal,
    );
    return asList(response.items);
  });
}

/**
 * Only the count of the reference row "Zugeordnete Geraete". The list itself,
 * and every device write, stays in `TenantDevicesCard` — this view frames that
 * card instead of re-implementing it.
 */
export function fetchTenDevicesSummary(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<TenDevicesSummary>> {
  return readTen(async () => {
    const response = await apiGet<TenDevicesResponse>(tenDevicesPath(tenantId), undefined, signal);
    const total =
      typeof response.total === "number" ? response.total : asList(response.items).length;
    return { total, capped: response.capped === true };
  });
}

export function fetchTenLaunchStatus(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<TenLaunchStatus>> {
  return readTen(() => apiGet<TenLaunchStatus>(tenLaunchStatusPath(tenantId), undefined, signal));
}

export function fetchTenAiCapabilities(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<TenAiCapabilities | null>> {
  return readTen(async () => {
    const response = await apiGet<TenAiCapabilitiesResponse>(
      tenAiCapabilitiesPath(tenantId),
      undefined,
      signal,
    );
    return response.aiCapabilities ?? null;
  });
}

export function fetchTenAiUsage(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenResult<TenAiUsage | null>> {
  return readTen(async () => {
    const response = await apiGet<TenAiUsageResponse>(tenAiUsagePath(tenantId), undefined, signal);
    return response.usage ?? null;
  });
}

/**
 * Eine Liste aus einer Antwort, die im Typ eine Liste ist, es zur Laufzeit
 * aber nicht sein muss (Owner-Entscheid P10: nie eine erfundene Zahl, aber
 * auch nie ein Absturz statt einer Aussage).
 */
function asList<T>(value: ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
  return Array.isArray(value) ? value : [];
}

export type { TenantLifecycleVerb };

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * Every key is its own exported array literal — A-GTCC-QUERY-KEY-CONSISTENCY1
 * reads the keys statically out of the source and can follow
 * `queryKey: TEN_TENANTS_KEY` to this literal, while a member access would
 * stay unreadable for it.
 *
 * A key WITH a tenant id is written out as a literal at the call site
 * (`["gtcc", "ten", "view", "tenant", id]`), not as `[...TEN_TENANT_KEY, id]`:
 * the static reader cannot follow a spread across module borders and would
 * read the whole key as two unknown segments — an invalidation that really
 * hits its fetch would then look like a miss, and a genuine miss would hide
 * behind the same two wildcards. The constants below stay the single place
 * where the prefix is decided; `TEN_QUERY_KEYS` composes them for callers that
 * need the key as a value.
 */
export const TEN_TENANTS_KEY = ["gtcc", "ten", "view", "tenants"] as const;
export const TEN_TENANT_KEY = ["gtcc", "ten", "view", "tenant"] as const;
export const TEN_MEMBERS_KEY = ["gtcc", "ten", "view", "members"] as const;
export const TEN_INVITATIONS_KEY = ["gtcc", "ten", "view", "invitations"] as const;
export const TEN_DEVICES_KEY = ["gtcc", "ten", "view", "devices"] as const;
export const TEN_LAUNCH_KEY = ["gtcc", "ten", "view", "launch"] as const;
export const TEN_AI_CAPS_KEY = ["gtcc", "ten", "view", "ai-capabilities"] as const;
export const TEN_AI_USAGE_KEY = ["gtcc", "ten", "view", "ai-usage"] as const;

export const TEN_QUERY_KEYS = {
  tenants: TEN_TENANTS_KEY,
  tenant: (tenantId: string) => [...TEN_TENANT_KEY, tenantId] as const,
  members: (tenantId: string) => [...TEN_MEMBERS_KEY, tenantId] as const,
  invitations: (tenantId: string) => [...TEN_INVITATIONS_KEY, tenantId] as const,
  devices: (tenantId: string) => [...TEN_DEVICES_KEY, tenantId] as const,
  launch: (tenantId: string) => [...TEN_LAUNCH_KEY, tenantId] as const,
  aiCapabilities: (tenantId: string) => [...TEN_AI_CAPS_KEY, tenantId] as const,
  aiUsage: (tenantId: string) => [...TEN_AI_USAGE_KEY, tenantId] as const,
} as const;
